/**
 * grid.ts — Grade interna de 12 colunas do Studio (Fase 2 do motor de composições)
 *
 * Módulo NEUTRO (sem DOM, React, Node ou Deno): é a única fonte de verdade da
 * grade, usada pelo editor, pelo renderizador web e pelo compilador HTML das
 * Edge Functions.
 *
 * Conceito para o usuário comum: em vez de pedir "quantas colunas", ele escolhe
 * uma PROPORÇÃO pronta (ex.: "duas iguais", "maior à esquerda"). Internamente
 * cada proporção vira uma soma de 12 colunas, o que permite composições
 * assimétricas sem expor números para quem não quer mexer nisso.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (motor de composições, Fase 2)
 */

// Sem imports de propósito: este módulo é lido também pelo Deno (Edge Functions),
// onde caminhos relativos precisariam de extensão. Os tipos abaixo espelham
// `StudioBreakpoint`/`StudioResponsiveValue` de `../types`.

/** Tamanhos de tela atendidos pelo Studio. */
type StudioBreakpoint = 'desktop' | 'tablet' | 'mobile';

/** Valor que pode variar por tamanho de tela. */
type StudioResponsiveValue<T> = T | { desktop?: T; tablet?: T; mobile?: T };

/** Resolve o valor da tela, herdando do tamanho maior para o menor. */
function resolveResponsive<T>(
  valor: StudioResponsiveValue<T> | undefined,
  tela: StudioBreakpoint,
): T | undefined {
  if (valor === undefined || valor === null) return undefined;
  if (typeof valor !== 'object' || Array.isArray(valor)) return valor as T;
  const mapa = valor as { desktop?: T; tablet?: T; mobile?: T };
  const cascata: StudioBreakpoint[] =
    tela === 'mobile' ? ['mobile', 'tablet', 'desktop']
    : tela === 'tablet' ? ['tablet', 'desktop']
    : ['desktop'];
  for (const chave of cascata) {
    const encontrado = mapa[chave];
    if (encontrado !== undefined) return encontrado;
  }
  return undefined;
}

/** Total de colunas da grade interna. */
export const STUDIO_GRID_COLUMNS = 12;

/** Uma proporção pronta da grade. */
export interface StudioGridPreset {
  /** Chave gravada no documento. */
  value: string;
  /** Rótulo exibido no painel, em linguagem simples. */
  label: string;
  /** Larguras em colunas (soma 12). Vazio = colunas iguais automáticas. */
  spans: number[];
}

/**
 * Proporções disponíveis. A ordem é a de exibição no painel.
 * `auto` mantém o comportamento antigo (colunas iguais pela quantidade de itens).
 */
export const STUDIO_GRID_PRESETS: StudioGridPreset[] = [
  { value: 'auto', label: 'Automático (colunas iguais)', spans: [] },
  { value: '12', label: 'Uma coluna', spans: [12] },
  { value: '6-6', label: 'Duas iguais', spans: [6, 6] },
  { value: '8-4', label: 'Maior à esquerda', spans: [8, 4] },
  { value: '4-8', label: 'Maior à direita', spans: [4, 8] },
  { value: '7-5', label: 'Levemente maior à esquerda', spans: [7, 5] },
  { value: '5-7', label: 'Levemente maior à direita', spans: [5, 7] },
  { value: '9-3', label: 'Conteúdo e barra lateral', spans: [9, 3] },
  { value: '3-9', label: 'Barra lateral e conteúdo', spans: [3, 9] },
  { value: '4-4-4', label: 'Três iguais', spans: [4, 4, 4] },
  { value: '6-3-3', label: 'Destaque e dois apoios', spans: [6, 3, 3] },
  { value: '3-6-3', label: 'Destaque no meio', spans: [3, 6, 3] },
  { value: '3-3-3-3', label: 'Quatro iguais', spans: [3, 3, 3, 3] },
];

/** Opções prontas para campos do tipo `select` no painel de propriedades. */
export const STUDIO_GRID_PRESET_OPTIONS = STUDIO_GRID_PRESETS.map(({ value, label }) => ({
  value,
  label,
}));

/** Encontra a proporção pela chave (volta para `auto` quando desconhecida). */
function acharPreset(chave: unknown): StudioGridPreset {
  const alvo = String(chave ?? 'auto');
  return STUDIO_GRID_PRESETS.find((p) => p.value === alvo) ?? STUDIO_GRID_PRESETS[0];
}

/**
 * Calcula a largura (em colunas de 12) de cada filho.
 *
 * - `auto` divide 12 igualmente pela quantidade de filhos (mínimo 1 coluna);
 * - proporções com menos entradas que filhos se repetem em ciclo, para que
 *   nenhum filho fique sem largura;
 * - qualquer valor fora de 1..12 é ajustado para dentro do intervalo.
 */
export function resolverLarguras(
  preset: unknown,
  quantidadeFilhos: number,
  colunasAutomaticas?: unknown,
): number[] {
  const total = Math.max(quantidadeFilhos, 0);
  if (total === 0) return [];

  const encontrado = acharPreset(preset);

  if (encontrado.value === 'auto' || encontrado.spans.length === 0) {
    // Sem proporção escolhida: usa a quantidade indicada (compatibilidade) ou
    // divide igualmente entre os filhos existentes.
    const colunas = Number(colunasAutomaticas) || total;
    const largura = Math.max(1, Math.min(STUDIO_GRID_COLUMNS, Math.floor(STUDIO_GRID_COLUMNS / Math.max(1, colunas))));
    return Array.from({ length: total }, () => largura);
  }

  return Array.from({ length: total }, (_, indice) => {
    const bruto = encontrado.spans[indice % encontrado.spans.length];
    return Math.max(1, Math.min(STUDIO_GRID_COLUMNS, Math.round(bruto)));
  });
}

/** Prefixo de classe por tamanho de tela. */
const PREFIXO: Record<StudioBreakpoint, string> = {
  desktop: 'st-gc',
  tablet: 'st-gc-t',
  mobile: 'st-gc-m',
};

/**
 * Monta as classes de largura de um filho para os três tamanhos de tela.
 * O valor da proporção pode ser simples ou responsivo (`StudioResponsiveValue`).
 */
export function classesDaColuna(
  preset: StudioResponsiveValue<string> | undefined,
  indice: number,
  quantidadeFilhos: number,
  colunasAutomaticas?: unknown,
): string {
  const classes: string[] = ['st-gc'];
  (['desktop', 'tablet', 'mobile'] as StudioBreakpoint[]).forEach((tela) => {
    // No computador sempre há largura (cai em "automático" quando nada foi escolhido);
    // tablet e celular só ganham classe quando houver ajuste próprio.
    const escolhido = resolveResponsive<string>(preset, tela) ?? (tela === 'desktop' ? 'auto' : undefined);
    if (escolhido === undefined) return;
    const larguras = resolverLarguras(escolhido, quantidadeFilhos, colunasAutomaticas);
    const largura = larguras[indice];
    if (largura) classes.push(`${PREFIXO[tela]}-${largura}`);
  });
  return classes.join(' ');
}
